/**
 * Flash changed visible rows after a Baxter write. Callers project the columns
 * they render. A tool signal arms the previous snapshot; query settlement
 * disarms even an unchanged refetch so a later unrelated edit cannot flash.
 */

import { useEffect, useRef, useState } from 'react';
import { useBaxterEffects } from './baxterEffects';

export interface HighlightRow {
  id: string;
  updated_at?: string;
  created_at?: string;
}

/** How long the CSS flash runs; clearing after it lets a SECOND edit re-flash the same row. */
const FLASH_MS = 1600;

const EMPTY: ReadonlySet<string> = new Set();

const stampsOf = (rows: HighlightRow[] | undefined) =>
  new Map((rows ?? []).map((row) => [row.id, JSON.stringify(row, (key, value) =>
    key === 'updated_at' || key === 'created_at' ? undefined : value)]));

export function useTurnHighlight<T extends HighlightRow>(
  rows: T[] | undefined,
  changedAt: number | null,
): ReadonlySet<string> {
  const settled = useBaxterEffects((s) => Object.values(s.changed).some((change) => change.at === changedAt && change.settled));
  const snapshot = useRef(stampsOf(rows));
  const armed = useRef<number | null>(null);
  const seenSignal = useRef(changedAt);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hits, setHits] = useState<ReadonlySet<string>>(EMPTY);

  // Declared FIRST so a commit that carries both a new `changedAt` and new rows
  // arms before the diff below reads the flag. `null` (a fresh turn) disarms, so
  // a call that moved nothing visible here can't leave the hook armed forever.
  useEffect(() => {
    if (seenSignal.current === changedAt) return;
    seenSignal.current = changedAt;
    armed.current = changedAt;
  }, [changedAt]);

  useEffect(() => {
    const stamps = stampsOf(rows);
    if (armed.current == null) {
      snapshot.current = stamps; // idle: the snapshot just tracks the truth
      return;
    }
    // Equal refetches may retain their old reference through structural sharing.
    const diff = new Set<string>();
    for (const [id, stamp] of stamps) {
      const before = snapshot.current.get(id);
      if (before === undefined || before !== stamp) diff.add(id);
    }
    const moved = diff.size > 0 || stamps.size !== snapshot.current.size; // size covers deletes
    if (!moved) { if (settled) armed.current = null; return; }
    snapshot.current = stamps;
    armed.current = null; // a pure delete diffs to nothing, and must still disarm
    if (!diff.size) return;
    setHits(diff);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setHits(EMPTY), FLASH_MS);
  }, [rows, settled]);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  return hits;
}
